import { useCallback, useState, useRef, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import DyanamicDropdown from "./DynamicDropdown";
import instance from "helpers/api";
import { Apis } from "services/api";
import { getSearchedSiteAssetReferences } from "services/models/modelDetails/modelAsset";
import { debounce, handleSort } from "helpers/utils";

// Minimum width to ensure dropdown is wide enough for content
const MIN_DROPDOWN_WIDTH = 600;

const useStyles = makeStyles()((theme) => ({
  // Add any specific styles here if needed
  dropdownContainer: {
    position: "relative",
    width: "100%"
  }
}));

const DefaultAssetReferenceDropdown = ({
  value,
  onChange,
  disabled,
  isError,
  label,
}) => {
  const { classes } = useStyles();
  const [assets, setAsset] = useState([]);
  const [page, setPage] = useState({ pageNo: 1, pageSize: 10, sortField:'', sortOrder:'' });
  const pageRef = useRef(page)
  const [count, setCount] = useState(0);
  const [inputWidth, setInputWidth] = useState(MIN_DROPDOWN_WIDTH);
  const dropdownRef = useRef(null);

  const {
    position,
    siteID,
    application: { showAssetReferences },
    customCaptions,
  } = JSON.parse(sessionStorage.getItem("me")) ||
    JSON.parse(localStorage.getItem("me"));

  // Measure the width of the input element
  useEffect(() => {
    if (dropdownRef.current) {
      const inputElement = dropdownRef.current.querySelector('.inputbox');
      if (inputElement) {
        // Use ResizeObserver to track changes in width
        const resizeObserver = new ResizeObserver(entries => {
          for (let entry of entries) {
            // Always use the larger of the minimum width or input width
            setInputWidth(Math.max(entry.contentRect.width, MIN_DROPDOWN_WIDTH));
          }
        });
        
        resizeObserver.observe(inputElement);
        
        // Initial measurement
        setInputWidth(Math.max(inputElement.offsetWidth, MIN_DROPDOWN_WIDTH));
        
        return () => {
          resizeObserver.disconnect();
        };
      }
    }
  }, []);

  useEffect(()=>{ pageRef.current= page},[JSON.stringify(page)])

  const fetchAssets = async (pNo = 1, prevData = [], sortField = "", sortOrder = "") => {
    // Ensure pageNumber is always passed to API
    let pageSearchField = `&&pageNumber=${pNo}&&pageSize=${page.pageSize}&&sortField=${sortField}&&sortOrder=${sortOrder}`;

    try {
      let result = await instance.get(
        `${Apis.SiteAssetReferencesForSite}?siteAppId=${position?.siteAppID}${pageSearchField}`
      );
      if (result.status) {
        // Combine previous data with new data for pagination
        setAsset([...prevData, ...result.data]);
      }
    } catch (e) {
      return;
    }
  };

  const fetchAssetCount = async () => {
    try {
      let result = await instance.get(`/api/SiteAssets/Count?siteId=${siteID}`);
      if (result.status) {
        setCount(result.data);
      }
    } catch (e) {
      return;
    }
  };

  const fetchAssetData = async () => {
    // Reset pagination state and asset list
    setPage((prev)=>({...prev, pageNo: 1, pageSize: 10 }));
    setAsset([]);
    await Promise.all([fetchAssets(1, [], page.sortField, page.sortOrder), fetchAssetCount()]);
  };

  const pageChange = async (p, prevData) => {
    const {current} = pageRef;
    // Fetch new page of assets
    await fetchAssets(p, prevData, current.sortField, current.sortOrder);
    // Update page state AFTER fetching to ensure the page number is correct
    setPage((prev)=>({...prev,pageNo: p, pageSize: page.pageSize }));
  };

  const handleServerSideSearch = useCallback(
    debounce(async (searchTxt) => {   
      if (searchTxt) {
        const response = await getSearchedSiteAssetReferences(
          position.siteAppID,
          1,
          20,
          searchTxt
        );
        if (response?.status) {
          // Reset page to 1 when searching
      setPage((prev)=>({...prev,pageNo: 1, pageSize: page.pageSize }));
          setAsset(response.data);
        }
      } else {
        // Reset to page 1 when search is cleared
        setAsset([]);
    setPage((prev)=>({...prev,pageNo: 1, pageSize: page.pageSize }));
        await fetchAssets(1, []);
      }
    }, 500),
    []
  );

  // Define headers and columns based on showAssetReferences flag
  const columnWidth = showAssetReferences ? "33%" : "50%";
  
  // Base header and columns (always include asset and description)
  const dataHeader = [
    {
      id: 1,
      name: `${customCaptions?.asset ?? "Asset"}`,
      width: columnWidth,
      sortField: "assetno"
    },
    {
      id: 3,
      name: "Description",
      width: showAssetReferences ? "34%" : columnWidth,
      sortField: "description"
    }
  ];
  
  const columns = [
    {
      id: 1,
      name: "assetName",
    },
    {
      id: 3,
      name: showAssetReferences ? "assetReferenceDescription" : "assetDescription",
    }
  ];
  
  // Insert asset reference column if showAssetReferences is true
  if (showAssetReferences) {
    dataHeader.splice(1, 0, {
      id: 2,
      name: `${customCaptions?.assetReference ?? "Asset Reference"}`,
      width: columnWidth,
      sortField: "referencename"
    });
    
    columns.splice(1, 0, {
      id: 2,
      name: "assetReferenceName",
    });
  }
  const handleCustomSort = async (sortField, sortOrder) => {
    let apiSortField = "";
    switch (sortField) {
      case "assetName":
        apiSortField = "assetno";
        break;
      case "assetReferenceName":
        apiSortField = "referencename";
        break;
      case "assetReferenceDescription":
        apiSortField = "description";
        break;
      default:
        apiSortField = sortField;
    }
    
    setPage({ pageNo: 1, pageSize: page.pageSize,sortField:apiSortField,sortOrder });
    setAsset([]);
    await fetchAssets(1, [], apiSortField, sortOrder);
  };

  return (
    <div className={classes.dropdownContainer} ref={dropdownRef}>
      <DyanamicDropdown
        width={"100%"}
        dropDownActiveWidth={`${inputWidth}px`}
        dataSource={assets}
        dataHeader={dataHeader}
        columns={columns}
        showHeader
        onChange={(val) => onChange(val.assetReferenceName)}
        selectedValue={{ name: value }}
        onPageChange={pageChange}
        page={page}
        selectdValueToshow="name"
        count={count}
        handleServerSideSort={handleCustomSort}
        isError={isError}
        handleServerSideSearch={handleServerSideSearch}
        isServerSide
        fetchData={fetchAssetData}
        disabled={!showAssetReferences}
        showClear
        onClear={() => onChange("")}
        label={label ?? customCaptions?.asset}
      />
    </div>
  );
};

export default DefaultAssetReferenceDropdown; 